import {useMemo, useState} from "preact/compat";
import {useEffect} from "preact/hooks";
import {useAppContext, useBehavioralContext} from "../../../../contexts";
import {v4 as uuidv4} from "uuid";
import {ATTENDANCE_PICKER_DATA_ENUM} from "../../../../utils";

export const usePicker = () => {
    // State to store the selected attendance type
    const [selectedAttendanceType, setSelectedAttendanceType] = useState("");
    // State to store the available attendance types
    const [availableAttendanceTypes, setAvailableAttendanceTypes] = useState([]);
    // Flags when the attendance block content is ready to be rendered
    const [isComponentReady, setIsComponentReady] = useState(false);

    // Retrieves store data from the app context
    const {storeLocation} = useAppContext();

    // This variable checks if there's only one attendance type available and, if so,
    // automatically assigns it to the selectedAttendanceType.
    // This makes it able to open its content selector by default when the page is loaded
    const singleAttendanceType = useMemo(() => {
        return availableAttendanceTypes.length === 1 ?
            availableAttendanceTypes[0].label :
            undefined;
    }, [availableAttendanceTypes]);

    // This variable checks if there's only one attendance type available
    const hasOnlyOneAttendanceType = useMemo(() => {
        return availableAttendanceTypes.length === 1;
    }, [availableAttendanceTypes]);

    // General cart data
    const {sidebarCartData} = useBehavioralContext();

    // Method to handle when the chosen attendance type is changed
    const handleAttendance = props => {
        // Destructured props
        const {type} = props;

        // Sets the new attendance type to the selectedAttendanceType state
        setSelectedAttendanceType(type);
    }

    // Formats the data to the needed one
    const formatData = props => {
        // Prevents unnecessary errors
        if (!props) {
            return;
        }

        // Destructured props
        const {variants} = props;

        // The variable in which data will be stored in
        let data = [];

        for (let i = 0; i < variants.length; i++) {
            const currentVariant = variants[i];

            if (currentVariant.stockStatus === 'Out Stock') {
                continue;
            }

            // The object item we will add to the attendance type
            const newItem = {
                id:          currentVariant.id,
                value:       currentVariant.id,
                label:       currentVariant.name,
                stockQty:    currentVariant.stockQty,
                stockStatus: currentVariant.stockStatus,
                region:      currentVariant.region,
                externalId:  currentVariant.externalId
            }

            // If the attendance type isn't in the data object yet, then we add it to it
            if (!data.find(dataItem => dataItem.label === currentVariant.type.label)) {
                data.push({
                    label: currentVariant.type.label,
                    ...ATTENDANCE_PICKER_DATA_ENUM[currentVariant.type.label],
                    items: [newItem]
                })

                continue;
            }

            // Gets the index of the already existing attendance type
            let itemIndex = data.findIndex(dataItem => {
                return dataItem.label === currentVariant.type.label;
            });

            // Appends the new item to it
            data[itemIndex].items.push(newItem);
        }

        // Loop to format the region data for each attendance type
        data.forEach(attendance => {
            let regions = [];

            for (let j = 0; j < attendance.items.length; j++) {
                let currentAttendance = attendance.items[j];

                // This is a validation for when no region is attached to a warehouse.
                // But as of August 22nd, this was asked to be deactivated and,
                // instead, to assign the products to a generic region,
                // just so we can display them to the user.
                if (!currentAttendance.region.name) {
                    const storeLocationName = storeLocation.name.split("-")[0];

                    // If the attendance type isn't in the data object yet, then we add it to it
                    if (!regions.find(region => region.name === storeLocationName)) {
                        regions.push({
                            id:    uuidv4(),
                            name:  storeLocationName,
                            items: [currentAttendance]
                        });

                        continue;
                    }

                    // Gets the index of the already existing attendance type
                    let regionIndex = regions.findIndex(region => {
                        return region.name === storeLocationName;
                    });

                    // Appends the new item to it
                    regions[regionIndex].items.push(currentAttendance);

                    continue;
                }

                // If the attendance type isn't in the data object yet, then we add it to it
                if (!regions.find(region => region.id === currentAttendance.region.id)) {
                    regions.push({
                        id:    currentAttendance.region.id,
                        name:  currentAttendance.region.name,
                        items: [currentAttendance]
                    });

                    continue;
                }

                // Gets the index of the already existing attendance type
                let regionIndex = regions.findIndex(region => {
                    return region.id === currentAttendance.region.id;
                });

                // Appends the new item to it
                regions[regionIndex].items.push(currentAttendance);
            }

            attendance["regions"] = regions;

            // Performs the last modifications to the final data object
            if (attendance.label !== "home_delivery") {
                delete attendance["items"];
            }
        });

        // Sets the formatted data into the availableAttendanceTypes state
        setAvailableAttendanceTypes(data);

        setIsComponentReady(true);
    }

    // Effect to trigger the "formatData" procedure
    useEffect(() => {
        if (!sidebarCartData.newProductData.productData) {
            return;
        }

        formatData(sidebarCartData.newProductData.productData);
    }, [sidebarCartData.newProductData]);

    // Effect to validate if there's only one attendance type available
    useEffect(() => {
        if (singleAttendanceType) {
            setSelectedAttendanceType(singleAttendanceType);
        }
    }, [singleAttendanceType]);

    return {
        sidebarCartData,
        selectedAttendanceType,
        availableAttendanceTypes,
        isComponentReady,
        hasOnlyOneAttendanceType,
        handleAttendance
    }
}
